import {useEffect, useMemo, useRef, useState, useCallback} from 'react'
import {useBasketStore, useCustomerStore, useGlobalStore} from '../store/hooks/useStore'
import {BeforeYouGoPage} from '../types/store/basket'
import {isSearchPage, predicate, shouldShowPage} from '../pages/before-you-go/helpers'
import {useSearchParams} from './use-search-params'
import {ProductModel} from '../store/ProductStore'
import {useHistory} from 'react-router-dom'
import {CHECKOUT_SUMMARY_PATH} from '../constants'

export type EnrichedBeforeYouGoPage = BeforeYouGoPage & {
  from: string | null
  to: string | null
}

type BeforeYouGoPageData = Map<BeforeYouGoPage['ID'], EnrichedBeforeYouGoPage>

export const PAGE_TYPES = {
  MISSED_OFFERS: 'missedOffers',
  FREQUENTLY_BOUGHT: 'frequentlyBought',
  FAVOURITES: 'favourites',
  SEARCH: 'search',
} as const

export const DEFAULT_PRODUCTS_PER_PAGE = 20

export const useBeforeYouGoPages = () => {
  const {beforeYouGoPages, missedOffers, basket} = useBasketStore()
  const params = useSearchParams()
  const {frequentlyBoughtProducts, wishlistHits} = useCustomerStore()
  const {searchClient, algoliaIndexBase, selectedAvailabilityDayOffset, selectedStoreId} = useGlobalStore()
  const history = useHistory()
  const algoliaIndex = `${algoliaIndexBase}__products__default`
  const index = searchClient.initIndex(algoliaIndex)
  const [pagesData, setPagesData] = useState<BeforeYouGoPage[]>([])
  let prodIds: string[] = []
  // Get a list of products in the basket at this stage (NO need to keep updating this every time we add to basket in this process)
  if(basket && basket.productItems) {
    prodIds = basket.productItems.reduce((acc, item) => {
        if (item.productId) {
          acc.push(item.productId)
        }
        return acc
      }, [] as string[])
  }
  const ids = useRef<string[]>([])

  ids.current.length === 0 ? (ids.current = prodIds) : ids.current

  const isInBasket = (p: ProductModel): boolean => predicate(p.id, ids.current)

  const pageData = useMemo(
    () => ({
      [PAGE_TYPES.MISSED_OFFERS]: {
        data: missedOffers,
        filteredData: null,
      },
      [PAGE_TYPES.FREQUENTLY_BOUGHT]: {
        data: frequentlyBoughtProducts,
        filteredData: frequentlyBoughtProducts.filter(isInBasket),
      },
      [PAGE_TYPES.FAVOURITES]: {
        data: wishlistHits,
        /* @TODO Wishlist data isn't being mapped to ProductModels correctly so we're getting conflicts with Hit -> productModel that need
        resolving as part of a future ticket (likely part of Algolia work)
        @ts-ignore */
        filteredData: wishlistHits.filter(isInBasket),
      },
    }),
    [frequentlyBoughtProducts, missedOffers, wishlistHits],
  )

  useEffect(() => {
    const isCategoryExists = async (
      id: BeforeYouGoPage['categoryID'],
      isHidden: BeforeYouGoPage['hideProductsInBasket'],
      min: BeforeYouGoPage['minProducts'],
    ) => {
      let exists

      const exclusionFilter = isHidden
        ? ids.current.map((id) => `objectID:-${id}`)
        : ''

      await index
        .search('', {
          facetFilters: [`categoryPageId:${id}`,exclusionFilter, `matrixAvailability.${selectedAvailabilityDayOffset}:-${selectedStoreId}`, `in_stock:true`], // store and day filters applied to results
          hitsPerPage: min,
        })
        .then(({hits}) => {
          if (shouldShowPage(hits.length, min)) {
            exists = true
          } else {
            exists = false
          }
        })
        .catch(() => {
          exists = false
        })

      return exists
    }

    const filterPages = async () => {
      const pagePromises = (beforeYouGoPages ?? []).map(async (page) => {
        const {pageType, categoryID, hideProductsInBasket, minProducts} = page

        if (isSearchPage(page)) {
          const exists = await isCategoryExists(categoryID, hideProductsInBasket, minProducts)
          return exists ? page : null
        }

        if (pageType === PAGE_TYPES.MISSED_OFFERS) {
          const {data} = pageData[pageType]
          return shouldShowPage(data?.incompleteOffers || 0, minProducts) ? page : null // Uses the min Products setting in the BYG custom object to control a min number of missed offers to trigger the page with
        }

        const {data, filteredData} = pageData[pageType as keyof typeof pageData]

        const filtered = hideProductsInBasket
          ? (filteredData as ProductModel[])
          : (data as ProductModel[])

        return shouldShowPage(filtered?.length || 0, minProducts) ? page : null
      })

      const pages = await Promise.all(pagePromises)

      const filteredPages = pages.filter(Boolean) as BeforeYouGoPage[]

      setPagesData(filteredPages)
    }

    filterPages()
  }, [])

  const pagesMap = pagesData.reduce<BeforeYouGoPageData>((acc, page, i, arr) => {
    const {ID: id} = page
    let enrichedPage: EnrichedBeforeYouGoPage

    if (i === 0) {
      enrichedPage = {...page, from: id, to: arr[i + 1]?.ID}
      return acc.set(id, enrichedPage)
    }

    if (i === arr.length - 1) {
      enrichedPage = {...page, from: arr[i].ID, to: null}
      return acc.set(id, enrichedPage)
    }

    enrichedPage = {...page, from: arr[i].ID, to: arr[i + 1]?.ID}
    return acc.set(id, enrichedPage)
  }, new Map())

  const toParams = params.get('to')

  const currentPage: EnrichedBeforeYouGoPage = toParams
    ? pagesMap?.get(toParams)
    : pagesMap?.values().next().value

  const nextPageUrl = useCallback(() => {
    !currentPage?.to
      ? history.push(CHECKOUT_SUMMARY_PATH)
      : history.push({search: `from=${currentPage.from}&to=${currentPage.to}`})
  }, [currentPage])

  return {currentPage, pageData, nextPageUrl, ids: ids.current}
}
